import { computed, ref } from "vue"
import { defineStore } from "pinia"

import type { Booking, CreateBookingInput, DaySummary, JournalDay, WorkingMaster } from "@/entities/booking"
import { bookingApi } from "@/entities/booking"
import type { Service } from "@/entities/service"
import { serviceApi } from "@/entities/service"
import type { TimeBlock, CreateTimeBlockInput } from "@/entities/time-block"
import { timeBlockApi } from "@/entities/time-block"
import { normalizeError } from "@/shared/api"
import { useSessionStore } from "@/entities/session/model/store"

import { civilDate } from "./dayMath"
import { mapBookingError } from "./mapBookingError"
import type { BookingErrorKey } from "./mapBookingError"
import { mapTimeBlockError } from "./mapTimeBlockError"
import type { TimeBlockErrorKey } from "./mapTimeBlockError"
import { buildMasterColorMap } from "./masterColumns"
import { minutesOfDayInTz, offWorkZones } from "./timeGrid"
import type { OffWorkZone } from "./timeGrid"

type ActiveSheet = "none" | "actions" | "reschedule" | "cancel" | "create" | "createBlock" | "deleteBlock" | "chooser"
type DayStatus = "idle" | "loading" | "error" | "ready"
type MutationStatus = "idle" | "pending"
type AvailabilityStatus = "idle" | "loading" | "ready" | "error"

export const useRecordsStore = defineStore("records", () => {
  const sessionStore = useSessionStore()

  // --- Computed from session ---

  const salonTz = computed(() => sessionStore.company?.timezone ?? "UTC")

  // --- Day state ---

  const bookings = ref<Booking[]>([])
  const workingMasters = ref<WorkingMaster[]>([])
  const totalMasterCount = ref<number>(0)
  const services = ref<Service[]>([])
  const timeBlocks = ref<TimeBlock[]>([])
  const dayStatus = ref<DayStatus>("idle")
  const daySummary = ref<DaySummary | null>(null)
  const selectedDate = ref<string>(civilDate(new Date(), salonTz.value))

  /** grid interval from JournalDay — used for gridBounds computation. */
  const _journalGrid = ref<JournalDay["grid"] | null>(null)

  // --- Derived day getters ---

  const bookingsByMaster = computed(() => {
    const map = new Map<string, Booking[]>()
    for (const booking of bookings.value) {
      const list = map.get(booking.masterId)
      if (list) {
        list.push(booking)
      } else {
        map.set(booking.masterId, [booking])
      }
    }
    return map
  })

  const timeBlocksByMaster = computed(() => {
    const map = new Map<string, TimeBlock[]>()
    for (const block of timeBlocks.value) {
      const list = map.get(block.masterId)
      if (list) {
        list.push(block)
      } else {
        map.set(block.masterId, [block])
      }
    }
    return map
  })

  const dayOffMasterIds = computed(() => {
    const ids = new Set<string>()
    for (const master of workingMasters.value) {
      if (master.intervals.length === 0) {
        ids.add(master.id)
      }
    }
    return ids
  })

  /**
   * Pixel grid bounds for the time grid.
   * startMin = fixed salon open (from grid.start) or 08:00 fallback.
   * endMin = max(salonClose, latestBookingEnd) or 22:00 fallback.
   */
  const gridBounds = computed<{ startMin: number; endMin: number }>(() => {
    const grid = _journalGrid.value
    const tz = salonTz.value

    if (!grid) {
      let fallbackEnd = 1320
      for (const block of timeBlocks.value) {
        const endMin = minutesOfDayInTz(block.endsAt, tz)
        if (endMin > fallbackEnd) fallbackEnd = endMin
      }
      return { startMin: 480, endMin: fallbackEnd }
    }

    // Project salon open/close from UTC RFC3339 to minutes-of-day in salon tz.
    const startMin = minutesOfDayInTz(grid.start, tz)
    const salonCloseMin = minutesOfDayInTz(grid.end, tz)

    // endMin = max(salonClose, latestBookingEnd, latestBlockEnd) so nothing is clipped.
    let latestEnd = salonCloseMin
    for (const booking of bookings.value) {
      const endMin = minutesOfDayInTz(booking.endsAt, tz)
      if (endMin > latestEnd) latestEnd = endMin
    }
    for (const block of timeBlocks.value) {
      const endMin = minutesOfDayInTz(block.endsAt, tz)
      if (endMin > latestEnd) latestEnd = endMin
    }

    return { startMin, endMin: latestEnd }
  })

  const offWorkZonesByMaster = computed<Map<string, OffWorkZone[]>>(() => {
    const map = new Map<string, OffWorkZone[]>()
    for (const master of workingMasters.value) {
      if (dayOffMasterIds.value.has(master.id)) continue
      map.set(master.id, offWorkZones(master.intervals, gridBounds.value, salonTz.value))
    }
    return map
  })

  const hasWorkingMasters = computed(() =>
    workingMasters.value.some((m) => m.intervals.length > 0),
  )

  const isDayEmpty = computed(
    () => dayStatus.value === "ready" && workingMasters.value.length === 0,
  )

  const isNoBookings = computed(
    () =>
      dayStatus.value === "ready" &&
      workingMasters.value.length > 0 &&
      bookings.value.length === 0,
  )

  const serviceMap = computed(() => {
    const map = new Map<string, string>()
    for (const s of services.value) {
      map.set(s.id, s.name)
    }
    return map
  })

  const masterColorMap = computed(() =>
    buildMasterColorMap(workingMasters.value, dayOffMasterIds.value),
  )

  // --- Availability state ---

  const availabilityStatus = ref<AvailabilityStatus>("idle")
  const availabilitySlots = ref<string[]>([])

  // --- Sheet state ---

  const activeSheet = ref<ActiveSheet>("none")
  const editingBooking = ref<Booking | undefined>(undefined)
  const createStartMin = ref<number | undefined>(undefined)

  // --- Mutation state ---

  const mutationStatus = ref<MutationStatus>("idle")
  const mutationErrorKey = ref<BookingErrorKey | undefined>(undefined)

  // --- Chooser sheet state ---

  const chooserMasterId = ref<string | undefined>(undefined)
  const chooserStartMin = ref<number | undefined>(undefined)

  // --- Block sheet state ---

  const createBlockMasterId = ref<string | undefined>(undefined)
  const createBlockStartMin = ref<number | undefined>(undefined)
  const editingBlock = ref<TimeBlock | undefined>(undefined)
  const blockMutationStatus = ref<MutationStatus>("idle")
  const blockErrorKey = ref<TimeBlockErrorKey | undefined>(undefined)

  // --- Navigation ---

  const activeMasterId = ref<string>("all")

  // --- Sheet actions ---

  function openActions(booking: Booking): void {
    editingBooking.value = booking
    mutationStatus.value = "idle"
    mutationErrorKey.value = undefined
    activeSheet.value = "actions"
  }

  function openReschedule(booking: Booking): void {
    editingBooking.value = booking
    mutationStatus.value = "idle"
    mutationErrorKey.value = undefined
    activeSheet.value = "reschedule"
  }

  function openCancel(booking: Booking): void {
    editingBooking.value = booking
    mutationStatus.value = "idle"
    mutationErrorKey.value = undefined
    activeSheet.value = "cancel"
  }

  function openCreate(): void {
    mutationStatus.value = "idle"
    mutationErrorKey.value = undefined
    activeSheet.value = "create"
  }

  function openChooser(masterId: string, startMin?: number): void {
    chooserMasterId.value = masterId
    chooserStartMin.value = startMin
    activeSheet.value = "chooser"
  }

  function closeSheet(): void {
    activeSheet.value = "none"
    editingBooking.value = undefined
    mutationStatus.value = "idle"
    mutationErrorKey.value = undefined
    editingBlock.value = undefined
    createBlockMasterId.value = undefined
    createBlockStartMin.value = undefined
    blockMutationStatus.value = "idle"
    blockErrorKey.value = undefined
    chooserMasterId.value = undefined
    chooserStartMin.value = undefined
    createStartMin.value = undefined
    activeMasterId.value = "all"
  }

  function setCreateStart(startMin?: number): void {
    createStartMin.value = startMin
  }

  function setActiveMaster(id: string): void {
    activeMasterId.value = id
  }

  function selectDate(date: string): void {
    selectedDate.value = date
    void loadDay(date)
  }

  // --- Day loading ---

  /** Lightweight refresh: only re-fetches bookings list; silent on failure. */
  async function refreshDay(): Promise<void> {
    const date = selectedDate.value
    try {
      const fresh = await bookingApi.listBookingsForDay(date)
      if (selectedDate.value !== date) return
      bookings.value = fresh
    } catch {
      // silent: refreshDay is always a background sync
    }
  }

  /**
   * Full refresh after concurrent_modification:
   * 1. Re-fetches bookings.
   * 2. Re-binds `editingBooking` from the fresh list.
   * 3. Clears `mutationErrorKey` so the user can retry.
   */
  async function reloadForConcurrency(): Promise<void> {
    await refreshDay()
    if (editingBooking.value) {
      const found = bookings.value.find((b) => b.id === editingBooking.value!.id)
      if (found) {
        editingBooking.value = found
      }
    }
    mutationErrorKey.value = undefined
  }

  let _loadController: AbortController | null = null

  /** Full day load: journal structure + bookings + services (non-fatal). */
  async function loadDay(date?: string): Promise<void> {
    const targetDate = date ?? selectedDate.value
    selectedDate.value = targetDate

    _loadController?.abort()
    const controller = new AbortController()
    _loadController = controller

    dayStatus.value = "loading"

    try {
      const [journalDay, bookingsList, blocksList] = await Promise.all([
        bookingApi.getJournalDay(targetDate, controller.signal),
        bookingApi.listBookingsForDay(targetDate, controller.signal),
        timeBlockApi.listForDay(targetDate, undefined, controller.signal),
      ])

      if (selectedDate.value !== targetDate) return

      workingMasters.value = journalDay.masters
      totalMasterCount.value = journalDay.totalMasterCount
      _journalGrid.value = journalDay.grid
      bookings.value = bookingsList
      timeBlocks.value = blocksList

      if (editingBooking.value) {
        const found = bookings.value.find((b) => b.id === editingBooking.value!.id)
        if (!found) {
          editingBooking.value = undefined
        }
      }

      try {
        services.value = await serviceApi.listServices()
      } catch {
        // services fetch is non-fatal; dayStatus still becomes "ready"
      }

      dayStatus.value = "ready"
    } catch {
      if (selectedDate.value !== targetDate) return
      dayStatus.value = "error"
    }
  }

  // --- Mutations ---

  async function submitReschedule(
    id: string,
    data: { newMasterId: string | null; newStartsAt: string | null },
    version: number,
  ): Promise<void> {
    mutationStatus.value = "pending"
    mutationErrorKey.value = undefined
    try {
      await bookingApi.rescheduleBooking(id, {
        version,
        newMasterId: data.newMasterId,
        newStartsAt: data.newStartsAt,
      })
      mutationStatus.value = "idle"
      closeSheet()
      await refreshDay()
    } catch (err) {
      mutationStatus.value = "idle"
      const appError = normalizeError(err)
      const mapped = mapBookingError(appError)
      if (mapped.surface === "inline") {
        mutationErrorKey.value = mapped.i18nKey
      }
      throw mapped
    }
  }

  async function submitCancel(
    id: string,
    version: number,
    reason?: string,
  ): Promise<void> {
    mutationStatus.value = "pending"
    mutationErrorKey.value = undefined
    try {
      await bookingApi.cancelBooking(id, {
        version,
        reason: reason ?? null,
      })
      mutationStatus.value = "idle"
      closeSheet()
      await refreshDay()
    } catch (err) {
      mutationStatus.value = "idle"
      const appError = normalizeError(err)
      const mapped = mapBookingError(appError)
      if (mapped.surface === "inline") {
        mutationErrorKey.value = mapped.i18nKey
      }
      throw mapped
    }
  }

  async function submitCreate(input: CreateBookingInput): Promise<void> {
    mutationStatus.value = "pending"
    mutationErrorKey.value = undefined
    try {
      await bookingApi.createBooking(input)
      mutationStatus.value = "idle"
      closeSheet()
      await refreshDay()
    } catch (err) {
      mutationStatus.value = "idle"
      const appError = normalizeError(err)
      const mapped = mapBookingError(appError)
      if (mapped.surface === "inline") {
        mutationErrorKey.value = mapped.i18nKey
      }
      throw mapped
    }
  }

  // --- Block actions ---

  /** Silent re-fetch of time blocks for the current day. */
  async function refreshBlocks(): Promise<void> {
    const date = selectedDate.value
    try {
      const fresh = await timeBlockApi.listForDay(date)
      if (selectedDate.value !== date) return
      timeBlocks.value = fresh
    } catch {
      // silent
    }
  }

  function openCreateBlock(masterId?: string, startMin?: number): void {
    createBlockMasterId.value = masterId
    createBlockStartMin.value = startMin
    blockMutationStatus.value = "idle"
    blockErrorKey.value = undefined
    activeSheet.value = "createBlock"
  }

  function openDeleteBlock(block: TimeBlock): void {
    editingBlock.value = block
    blockMutationStatus.value = "idle"
    blockErrorKey.value = undefined
    activeSheet.value = "deleteBlock"
  }

  async function submitCreateBlock(input: CreateTimeBlockInput): Promise<void> {
    blockMutationStatus.value = "pending"
    blockErrorKey.value = undefined
    try {
      await timeBlockApi.create(input)
      blockMutationStatus.value = "idle"
      closeSheet()
      await refreshBlocks()
    } catch (err) {
      blockMutationStatus.value = "idle"
      const appError = normalizeError(err)
      const mapped = mapTimeBlockError(appError)
      if (mapped.surface === "inline") {
        blockErrorKey.value = mapped.i18nKey
      }
      throw mapped
    }
  }

  async function submitDeleteBlock(id: string): Promise<void> {
    blockMutationStatus.value = "pending"
    blockErrorKey.value = undefined
    try {
      await timeBlockApi.remove(id)
      blockMutationStatus.value = "idle"
      closeSheet()
      await refreshBlocks()
    } catch (err) {
      blockMutationStatus.value = "idle"
      const appError = normalizeError(err)
      const mapped = mapTimeBlockError(appError)
      // 404 = already deleted: close sheet + refresh, then surface as toast
      closeSheet()
      await refreshBlocks()
      throw mapped
    }
  }

  // --- Availability ---

  let _availabilitySeq = 0

  async function loadAvailability(
    masterId: string,
    serviceId: string,
    date: string,
  ): Promise<void> {
    const seq = ++_availabilitySeq
    availabilityStatus.value = "loading"
    try {
      const slots = await bookingApi.getAvailability(masterId, serviceId, date)
      if (seq !== _availabilitySeq) return
      availabilityStatus.value = "ready"
      availabilitySlots.value = slots
    } catch {
      if (seq !== _availabilitySeq) return
      availabilityStatus.value = "error"
    }
  }

  return {
    // State
    bookings,
    workingMasters,
    totalMasterCount,
    services,
    timeBlocks,
    dayStatus,
    daySummary,
    selectedDate,
    availabilityStatus,
    availabilitySlots,
    activeSheet,
    editingBooking,
    mutationStatus,
    mutationErrorKey,
    createBlockMasterId,
    createBlockStartMin,
    editingBlock,
    blockMutationStatus,
    blockErrorKey,
    activeMasterId,
    chooserMasterId,
    chooserStartMin,
    createStartMin,
    // Computed
    salonTz,
    bookingsByMaster,
    timeBlocksByMaster,
    dayOffMasterIds,
    offWorkZonesByMaster,
    hasWorkingMasters,
    isDayEmpty,
    isNoBookings,
    serviceMap,
    masterColorMap,
    gridBounds,
    // Actions
    openActions,
    openReschedule,
    openCancel,
    openCreate,
    openChooser,
    setCreateStart,
    openCreateBlock,
    openDeleteBlock,
    closeSheet,
    selectDate,
    setActiveMaster,
    refreshDay,
    refreshBlocks,
    reloadForConcurrency,
    loadDay,
    submitReschedule,
    submitCancel,
    submitCreate,
    submitCreateBlock,
    submitDeleteBlock,
    loadAvailability,
  }
})

// Backward-compat alias — unmodifiable spec files import `useJournalStore`
export { useRecordsStore as useJournalStore }
